import React from 'react';
import { Box, Typography } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';

import { Button } from '@occc/ui-design-system';

import { DevicePreviewFrame } from './device-preview-frame';

import { type AspectRatio } from '../../../touchpoint.constants';

type ImagePreviewCardProps = {
  aspectRatio: AspectRatio;
  imageUrl?: string;
  altText?: string;
  backgroundImage: string;
  children?: React.ReactNode; // For custom content like the 1x1 split layout
};

const imageStyles: Record<AspectRatio, React.CSSProperties> = {
  '4x3': {
    width: '377px',
    height: '263px',
    objectFit: 'cover',
    objectPosition: 'center',
    borderRadius: '9px',
  },
  '1x1': {
    width: '377.39px',
    height: '263.58px',
  },
  '9x16': {
    width: '154px',
    height: '247px',
    objectFit: 'cover',
    objectPosition: 'center',
    borderRadius: '4px',
  },
};

// Helper components
const UploadInterface = ({ aspectRatio }: { aspectRatio: AspectRatio }) => {
  const { width, height } = imageStyles[aspectRatio];

  return (
    <Box
      sx={{
        backgroundColor: 'common.white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        textAlign: 'center',
        width,
        height,
        borderRadius: aspectRatio === '9x16' ? '4px' : '9px',
        zIndex: 1000,
      }}
    >
      <UploadIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
      <Typography variant='body2' color='text.secondary'>
        Drag and Drop
      </Typography>
      <Typography variant='body2' color='text.secondary'>
        OR
      </Typography>
      <Button variant='contained' size='small'>
        BROWSE FILES
      </Button>
    </Box>
  );
};

const Label = ({ aspectRatio }: { aspectRatio: AspectRatio }) => (
  <Box sx={{ py: 1 }}>
    <Typography variant='body2' color='text.secondary'>
      {aspectRatio} image
    </Typography>
  </Box>
);

const ImagePreview = ({
  imageUrl,
  altText,
  aspectRatio,
}: {
  imageUrl: string;
  altText: string;
  aspectRatio: AspectRatio;
}) => <Box component='img' src={imageUrl} alt={altText} sx={imageStyles[aspectRatio]} />;

export const ImagePreviewCard = ({
  aspectRatio,
  imageUrl,
  altText,
  backgroundImage,
  children,
}: ImagePreviewCardProps) => {
  const renderContent = () => {
    // If children are provided (like SplitLayoutPreview), use them
    if (children) {
      return children;
    }

    // If imageUrl and altText are provided, show the image
    if (imageUrl && altText) {
      return <ImagePreview imageUrl={imageUrl} altText={altText} aspectRatio={aspectRatio} />;
    }

    // Otherwise, show upload interface
    return (
      <DevicePreviewFrame aspectRatio={aspectRatio} backgroundImage={backgroundImage}>
        <UploadInterface aspectRatio={aspectRatio} />
      </DevicePreviewFrame>
    );
  };

  return (
    <Box>
      <Label aspectRatio={aspectRatio} />
      <DevicePreviewFrame aspectRatio={aspectRatio} backgroundImage={backgroundImage}>
        {renderContent()}
      </DevicePreviewFrame>
    </Box>
  );
};
